import { useEffect, useState, type ReactNode } from 'react';
import { Check, Pencil, RotateCcw, X } from 'lucide-react';
import { locateQuietly } from '../lib/locate';
import { useMobileShell } from '../lib/platform';
import { MY_LOCATION, myLocation, placePoint, setOffTonight } from '../lib/tripEnds';
import { useTrip } from '../state/trip';
import PlaceSearch from './PlaceSearch';

interface Props {
  which: 'start' | 'end';
  /** 'card' on the My trip page, 'ticket' inside the punched ticket. */
  variant?: 'card' | 'ticket';
}

/**
 * The first or last row of a trip: where you set off from (your location or an address) and
 * where you finish (an address, or back to the start). Tap to change.
 */
export default function TripEndRow({ which, variant = 'card' }: Props) {
  const { trip, setEnds } = useTrip();
  const [editing, setEditing] = useState(false);
  const isStart = which === 'start';
  const point = isStart ? trip.start : trip.end;
  const ticket = variant === 'ticket';

  // "My location" means where you are when you set off: on the website, refresh it when the page
  // opens (no prompt). The phone layout takes it from its own GPS watch instead.
  const shell = useMobileShell();
  const mine = isStart && !!trip.start?.mine;
  useEffect(() => {
    if (!mine || shell || trip.punched.length || setOffTonight(trip.start)) return;
    let live = true;
    void locateQuietly().then((at) => live && at && setEnds({ start: myLocation(at) }));
    return () => {
      live = false;
    };
  }, [mine]);

  const clear = () => setEnds(isStart ? { start: null } : { end: null, endAtStart: false });

  const title = isStart ? 'Start' : 'Finish';
  const loop = !isStart && trip.endAtStart;
  const sub = loop
    ? trip.start?.mine
      ? 'Where you were when you headed out'
      : trip.start
        ? 'Back where you started'
        : 'Wherever you set off from'
    : isStart && point?.mine
      ? point.lat === null
        ? 'Wherever you are when you head out'
        : 'Your location when you head out'
      : null;
  const label = loop ? (trip.start && !trip.start.mine ? trip.start.label : 'Back to the start') : (point?.label ?? null);
  const done = !isStart && trip.finished;

  return (
    <div className={ticket ? 'rounded-xl px-2 py-2' : 'card p-3'}>
      <div className="flex items-center gap-3">
        <span
          className={`flex size-8 flex-none items-center justify-center rounded-md border-2 border-asphalt text-sm font-extrabold ${
            done ? 'bg-line-2 text-muted' : 'bg-cream text-asphalt'
          }`}
          aria-hidden
        >
          {done ? <Check size={16} /> : isStart ? 'S' : 'F'}
        </span>
        <button type="button" className="flex min-w-0 flex-1 flex-col text-left" onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">{title}</span>
          {label ? (
            <>
              <span className={`truncate font-bold ${done ? 'text-muted line-through decoration-amber/70' : 'text-cream'}`}>{label}</span>
              {sub && <span className="truncate text-[13px] text-dim">{sub}</span>}
            </>
          ) : (
            <span className="truncate font-semibold text-amber">
              {isStart ? 'Add a starting point' : 'Add where you finish'}
            </span>
          )}
        </button>
        {label ? (
          <div className="flex flex-none gap-1">
            <RowButton label={`Change the ${title.toLowerCase()}`} onClick={() => setEditing((e) => !e)}>
              <Pencil size={16} aria-hidden />
            </RowButton>
            <RowButton label={`Remove the ${title.toLowerCase()}`} onClick={clear}>
              <X size={18} aria-hidden />
            </RowButton>
          </div>
        ) : null}
      </div>

      {editing && (
        <div className="mt-3 flex flex-col gap-3 border-t border-line pt-3">
          {!isStart && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm ${trip.endAtStart ? 'btn-primary' : ''}`}
                onClick={() => {
                  setEnds({ endAtStart: true, end: null });
                  setEditing(false);
                }}
              >
                <RotateCcw size={16} aria-hidden /> Back to the start
              </button>
            </div>
          )}
          {/* The start field has the "Use my location" crosshair inside it. */}
          <PlaceSearch
            label={isStart ? 'Start from' : 'Or an address'}
            value={isStart ? (trip.start ? asWaypoint(trip.start) : null) : trip.end && !trip.endAtStart ? asWaypoint(trip.end) : null}
            placeholder={isStart ? 'Your location, home, a hotel, a town' : 'Home, a hotel, a town'}
            locate={isStart}
            onChange={(w) => {
              if (!w) return;
              if (isStart) setEnds({ start: w.label === MY_LOCATION ? myLocation(w) : placePoint(w) });
              else setEnds({ end: placePoint(w), endAtStart: false });
              setEditing(false);
            }}
          />
          {isStart && (
            <p className="text-[12px] text-dim">
              Your location stays on this device. A shared link just says &ldquo;start from your location&rdquo;.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function asWaypoint(p: { label: string; lat: number | null; lng: number | null }) {
  return p.lat !== null && p.lng !== null ? { label: p.label, lat: p.lat, lng: p.lng } : null;
}

function RowButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="inline-flex size-10 items-center justify-center rounded-lg border border-line-2 text-cream">
      {children}
    </button>
  );
}
